import React from "react";
import { Row } from "../../../components/row";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/table";
import type { MerchantModel } from "../types/Merchant";

type MerchantTableProps = {
    data: MerchantModel[] | null;
    maxHeight?: string;
    maxWidth?: string;
    scale?: number;
};

// Category color mapping (same as in MerchantMap and MerchantCard)
const getCategoryColor = (categoryName?: string): string => {
    const categoryColorMap: Record<string, string> = {
        'Food & Beverage': '#ff6900',
        'Health & Wellness': '#00c950',
        'Retail': '#ad46ff',
        'Beauty & Personal care': '#f6339a',
        'Education': '#3b82f6',
        'Grocery': '#14b8a6',
        'Auto': '#ef4444',
        'Transport': '#f59e0b',
        'Travel': '#06b6d4',
        'Entertainment': '#3339f6',
    };
    return categoryColorMap[categoryName || ''] || '#6a7282'; // Default gray
};

export default function MerchantTable({ data: merchants, maxHeight = "400px", maxWidth = "100%", scale = 1 }: MerchantTableProps) {
    // Sort merchants by name for consistent display
    const sortedMerchants = React.useMemo(() => {
        if (!merchants) return [];
        return [...merchants].sort((a, b) =>
            a.merchant_name.localeCompare(b.merchant_name)
        );
    }, [merchants]);

    return (
        <div style={{ maxWidth }} className="mt-5 w-full border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="min-h-[10px]"></div>
            <span className="px-6 py-6">Merchant List</span>
            <Row style={{ transform: `scale(${scale})`, transformOrigin: "top left" }} className="mt-5">
                <Table style={{ maxHeight }}>
                    <TableHeader className="bg-[#F7F8FA] dark:bg-gray-700 sticky top-[-5px] z-10">
                        <TableRow>
                            <TableHead className="text-[#364153] dark:text-gray-100 font-bold">Merchant Name</TableHead>
                            <TableHead className="text-[#364153] dark:text-gray-100 font-bold">Category</TableHead>
                            <TableHead className="text-[#364153] dark:text-gray-100 font-bold">Address</TableHead>
                            <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold">Coupons</TableHead>
                            <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold">Redemptions</TableHead>
                            {sortedMerchants.some(m => m.active_users !== undefined) && (
                                <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold">Active Users</TableHead>
                            )}
                            {sortedMerchants.some(m => m.engagement_rate !== undefined) && (
                                <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold">Engagement Rate</TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedMerchants.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center text-gray-500 dark:text-gray-400 py-8">
                                    No merchants found
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedMerchants.map((merchant: MerchantModel) => {
                                const categoryColor = getCategoryColor(merchant.category?.name);
                                return (
                                    <TableRow key={merchant.merchant_id}>
                                        <TableCell className="font-bold">
                                            {merchant.merchant_name}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: categoryColor }}
                                                />
                                                <span>{merchant.category?.name || "Unknown"}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                                            {merchant.address}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {merchant.total_coupons || 0}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {merchant.total_redemptions || 0}
                                        </TableCell>
                                        {sortedMerchants.some(m => m.active_users !== undefined) && (
                                            <TableCell className="text-center">
                                                {merchant.active_users !== undefined ? merchant.active_users : "-"}
                                            </TableCell>
                                        )}
                                        {sortedMerchants.some(m => m.engagement_rate !== undefined) && (
                                            <TableCell className="text-center">
                                                {merchant.engagement_rate !== undefined
                                                    ? `${merchant.engagement_rate.toFixed(1)}%`
                                                    : "-"}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Row>
        </div>
    );
}
